import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Gift, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  HelpCircle,
  ArrowRight,
  Baby,
  Cake,
  PartyPopper,
  Gem,
  Home,
  User,
  UserCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Gift event options
const GIFT_EVENTS = [
  { value: 'compleanno', label: 'Compleanno', icon: Cake },
  { value: 'matrimonio', label: 'Matrimonio', icon: Gem },
  { value: 'anniversario', label: 'Anniversario', icon: Heart },
  { value: 'battesimo', label: 'Battesimo', icon: Baby },
  { value: 'laurea', label: 'Laurea', icon: Star },
  { value: 'nascita', label: 'Nascita', icon: Baby },
  { value: 'addio_celibato', label: 'Addio al celibato/nubilato', icon: PartyPopper },
  { value: 'pensionamento', label: 'Pensionamento', icon: Gift },
  { value: 'nuovo_lavoro', label: 'Nuovo lavoro', icon: Sparkles },
  { value: 'nuova_casa', label: 'Nuova casa', icon: Home },
  { value: 'natale', label: 'Natale', icon: Gift },
  { value: 'san_valentino', label: 'San Valentino', icon: Heart },
  { value: 'festa_mamma', label: 'Festa della mamma', icon: Heart },
  { value: 'festa_papa', label: 'Festa del papà', icon: Heart },
  { value: 'altro', label: 'Altro evento', icon: Gift },
];

// Gift recipient options
const GIFT_RECIPIENTS = [
  { value: 'uomo', label: 'Per un uomo', icon: User },
  { value: 'donna', label: 'Per una donna', icon: UserCircle },
  { value: 'coppia', label: 'Per una coppia', icon: Users },
  { value: 'neonato', label: 'Per un neonato', icon: Baby },
  { value: 'bambino', label: 'Per un bambino', icon: Baby },
  { value: 'bambina', label: 'Per una bambina', icon: Baby },
  { value: 'ragazzo', label: 'Per un ragazzo', icon: User },
  { value: 'ragazza', label: 'Per una ragazza', icon: UserCircle },
  { value: 'mamma', label: 'Per mia mamma', icon: Heart },
  { value: 'papa', label: 'Per mio papà', icon: Heart },
  { value: 'nonni', label: 'Per i nonni', icon: Users },
  { value: 'amico', label: 'Per un amico/a', icon: Users },
  { value: 'collega', label: 'Per un collega', icon: User },
  { value: 'me_stesso', label: 'Per me stesso', icon: Sparkles },
  { value: 'altro', label: 'Altro', icon: Gift },
];

// Budget options - aligned with RequestsPage
const BUDGET_OPTIONS = [
  { value: '0-50', label: '€0 - €50', description: 'Piccoli oggetti' },
  { value: '50-100', label: '€50 - €100', description: 'Accessori semplici' },
  { value: '100-250', label: '€100 - €250', description: 'Creazioni base' },
  { value: '250-500', label: '€250 - €500', description: 'Pezzi elaborati' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Lavori complessi' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Pezzi unici' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Alto artigianato' },
  { value: '5000+', label: '€5.000+', description: 'Lusso e collezioni' },
];

// Timeline options - aligned with RequestsPage
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: '3months', label: '3 Mesi', description: 'Senza fretta' },
  { value: '6months', label: '6 Mesi', description: 'Progetto lungo' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example gifts for showcase
const GIFT_EXAMPLES = [
  {
    idea: "Regalo anniversario 10 anni insieme",
    result: "Collana artigianale con coordinate del primo incontro",
    image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400&h=300&fit=crop",
    category: "Gioielli"
  },
  {
    idea: "Regalo compleanno 50 anni papà",
    result: "Portafoglio in pelle inciso con dedica personale",
    image: "https://images.unsplash.com/photo-1627123424574-724758594e93?w=400&h=300&fit=crop",
    category: "Pelletteria"
  },
  {
    idea: "Regalo matrimonio amici",
    result: "Quadro personalizzato con ritratto stilizzato",
    image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=300&fit=crop",
    category: "Arte"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho trovato un regalo perfetto che non avrei mai trovato online. L'artigiano ha capito esattamente cosa volevo!",
    author: "Marco R.",
    location: "Milano"
  },
  {
    text: "Super semplice da usare. Ho ricevuto 5 proposte diverse e ho scelto quella che mi ha emozionato di più.",
    author: "Laura B.",
    location: "Roma"
  },
  {
    text: "Mia moglie è rimasta senza parole. Un regalo unico che racconta la nostra storia.",
    author: "Giuseppe M.",
    location: "Napoli"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Puoi descrivere la tua idea e ricevere proposte senza alcun costo."
  },
  {
    question: "Sono obbligato a comprare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma e decidi solo se trovi qualcosa che ti convince davvero."
  },
  {
    question: "Quanto tempo ci vuole per ricevere le proposte?",
    answer: "Le prime risposte arrivano generalmente entro 24-48 ore. Gli artigiani più attivi rispondono anche in poche ore."
  },
  {
    question: "Come vengono selezionati gli artigiani?",
    answer: "Tutti gli artigiani su Rarifex sono verificati e producono esclusivamente Made in Italy. Valutiamo qualità, affidabilità e recensioni."
  },
  {
    question: "Posso chattare con gli artigiani?",
    answer: "Sì, una volta ricevuta una proposta puoi chattare direttamente con l'artigiano per chiarire dettagli o richiedere modifiche."
  }
];

export default function GiftLandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    gift_event: '',
    gift_recipient: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Regalo Personalizzato | Artigiani Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `gift-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la tua idea per il regalo');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      // Save form data to localStorage for after login
      localStorage.setItem('rarifex_pending_gift_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const eventLabel = GIFT_EVENTS.find(e => e.value === formData.gift_event)?.label || '';
      const recipientLabel = GIFT_RECIPIENTS.find(r => r.value === formData.gift_recipient)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (eventLabel) details.push(`🎉  Occasione: ${eventLabel}`);
      if (recipientLabel) details.push(`👤  Per chi: ${recipientLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `Regalo ${eventLabel || 'personalizzato'}`,
        description: fullDescription,
        category: 'Regalo personalizzato',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      // Use backend API to bypass RLS issues
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('gift-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Regalo Personalizzato | Artigiani Italiani Creano il Tuo Regalo Unico | Rarifex</title>
        <meta name="description" content="Hai un'idea per un regalo unico? Gli artigiani italiani lo realizzano per te. Descrivi cosa vuoi e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="regalo personalizzato, regalo su misura, artigianato italiano, regalo unico, idee regalo, regalo artigianale, made in italy" />
        <link rel="canonical" href="https://www.rarifex.com/regalo-personalizzato" />
        <meta property="og:title" content="Regalo Personalizzato | Artigiani Italiani | Rarifex" />
        <meta property="og:description" content="Hai un'idea per un regalo unico? Gli artigiani italiani lo realizzano per te. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/regalo-personalizzato" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Gift className="w-4 h-4" />
                    Regalo su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Hai un'idea per un{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
                      regalo unico?
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Gli artigiani italiani lo realizzano per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi cosa vuoi e ricevi proposte personalizzate da artigiani selezionati.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani italiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>Nessun obbligo</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="gift-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo della tua idea
                        </Label>
                        <Input
                          placeholder="Es: Regalo anniversario per mia moglie"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il regalo che hai in mente *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... anche in modo semplice. Cosa vorresti regalare? Per quale occasione? Hai preferenze su materiali o stile?"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                        />
                      </div>
                      
                      {/* Gift Event & Recipient */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Per quale evento?
                          </Label>
                          <select
                            value={formData.gift_event}
                            onChange={(e) => handleInputChange('gift_event', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                          >
                            <option value="">Seleziona...</option>
                            {GIFT_EVENTS.map(event => (
                              <option key={event.value} value={event.value}>{event.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Per chi è?
                          </Label>
                          <select
                            value={formData.gift_recipient}
                            onChange={(e) => handleInputChange('gift_recipient', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                          >
                            <option value="">Seleziona...</option>
                            {GIFT_RECIPIENTS.map(rec => (
                              <option key={rec.value} value={rec.value}>{rec.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-rose-300 hover:bg-rose-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica immagini ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white shadow-lg shadow-rose-200"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Gift className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Non sai cosa regalare?
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-rose-400">✗</span>
                  I regali "classici" sono sempre uguali
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-rose-400">✗</span>
                  Vuoi qualcosa di davvero personale
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-rose-400">✗</span>
                  Non trovi nulla che rappresenti davvero quella persona
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                È normale. Succede a tutti.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Creiamo qualcosa di{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
                  unico
                </span>
                , partendo dalla tua idea
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Con noi non devi cercare tra centinaia di prodotti.
                <br />
                <strong className="text-slate-800">Parti da zero.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-rose-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Racconti la tua idea</h3>
                  <p className="text-slate-600">Descrivi cosa hai in mente, anche in modo semplice</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Artigiani ti propongono</h3>
                  <p className="text-slate-600">Ricevi proposte personalizzate da artigiani italiani</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli quella perfetta</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a realtà
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri artigiani
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {GIFT_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-rose-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-rose-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-rose-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scrivi cosa vuoi</h3>
                    <p className="text-slate-600">
                      Descrivi la tua idea per il regalo. Non devi essere preciso, anche una descrizione semplice va benissimo.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte</h3>
                    <p className="text-slate-600">
                      Artigiani selezionati vedono la tua richiesta e ti inviano proposte personalizzate con foto e preventivi.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli liberamente</h3>
                    <p className="text-slate-600">
                      Confronta le proposte, chatta con gli artigiani se hai domande. Nessun obbligo, scegli solo se ti convince.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché funziona
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Ogni regalo è unico</h3>
                    <p className="text-sm text-slate-600">Creato apposta per te e la persona che ami</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <Sparkles className="w-8 h-8 text-amber-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Realizzato su misura</h3>
                    <p className="text-sm text-slate-600">Personalizzabile in ogni dettaglio</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Artigiani verificati</h3>
                    <p className="text-sm text-slate-600">Solo Made in Italy di qualità</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-rose-50">
                  <Zap className="w-8 h-8 text-rose-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-semibold text-slate-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-rose-50 to-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Gift className="w-16 h-16 text-rose-500 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Hai già un'idea?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Trasformiamola in realtà.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600 text-white shadow-lg shadow-rose-200"
              >
                <Gift className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli artigiani, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/regalo-personalizzato')}
                className="flex-1 bg-rose-500 hover:bg-rose-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
